import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useOwnSlackApp, useOwnSlackAppActions } from "@/hooks/agent/useChatChannels";
import { Agent } from "@/types/agent/agent";
import { OwnSlackApp, OwnSlackAppCredentials } from "@/types/agent/chat-channels";

const PRIMARY_BUTTON =
  "min-h-[44px] bg-gradient-to-r from-genie-primary to-genie-secondary hover:from-genie-primary-hover hover:to-genie-secondary text-white font-semibold px-6 shadow-lg shadow-genie-primary/30 hover:shadow-xl hover:shadow-genie-primary/40 hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:hover:scale-100 disabled:hover:shadow-lg";

const EMPTY_CREDENTIALS: OwnSlackAppCredentials = { slackAppId: "", clientId: "", clientSecret: "", signingSecret: "" };

const installLinkFor = (app: OwnSlackApp) => `${window.location.origin}/connect/slack/install/${app.id}`;

async function copyText(text: string, success: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(success);
  } catch {
    toast.error("Failed to copy");
  }
}

export default function SlackOwnAppSection({ genie }: { genie: Agent }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: state, isLoading } = useOwnSlackApp(genie.id);
  const { create } = useOwnSlackAppActions(genie.id);
  const app = state?.app ?? null;

  const handleStart = () => {
    create.mutate(undefined, { onError: (error: Error) => toast.error(error.message || "Couldn't start the setup") });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full min-h-[44px] px-5 py-4 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
      >
        <span className="text-sm font-semibold text-gray-700">Advanced: use your own Slack app</span>
        <span className="text-sm font-medium text-genie-primary shrink-0">{isExpanded ? "Hide" : "Show"}</span>
      </button>

      {isExpanded && (
        <div className="px-5 py-4 border-t space-y-6">
          <p className="text-sm text-gray-600">
            More control over branding, more responsibility. You'll create and own a Slack app: its name, icon and
            description are yours, and people mention it as @{genie.name}. Setting it up takes about 10 minutes and
            Slack admin rights. You're responsible for keeping it running: if you delete it or change its settings,
            your customers lose it, and when we add features we may ask you to update it. Until you submit it to the
            Slack Marketplace yourself, Slack shows your customers "not approved by Slack" when they install it.
          </p>

          {isLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
            </div>
          ) : !state?.can_manage ? (
            <p className="text-sm text-gray-500">
              {app ? `${genie.name} has its own Slack app. ` : ""}Only the genie's owner can manage this.
            </p>
          ) : !app && !state.is_plan_eligible ? (
            <div className="text-sm text-gray-600">
              Your own Slack app is part of our paid plans.{" "}
              <Link to="/pricing" className="text-genie-primary hover:underline font-medium">
                See plans
              </Link>
            </div>
          ) : !app ? (
            <Button onClick={handleStart} disabled={create.isPending} className={PRIMARY_BUTTON}>
              {create.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Set up your own Slack app
            </Button>
          ) : app.status === "ready" ? (
            <ReadyApp genie={genie} app={app} />
          ) : (
            <SetupSteps genie={genie} app={app} />
          )}
        </div>
      )}
    </div>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r from-genie-primary to-genie-secondary text-white text-xs font-semibold flex items-center justify-center">
        {number}
      </span>
      <div className="min-w-0 flex-1 text-sm text-gray-600">
        <p className="font-semibold text-gray-900">{title}</p>
        <div className="mt-1 space-y-3">{children}</div>
      </div>
    </li>
  );
}

function SetupSteps({ genie, app }: { genie: Agent; app: OwnSlackApp }) {
  const installLink = installLinkFor(app);

  return (
    <div className="space-y-6">
      <ol className="space-y-5">
        <Step number={1} title="Create the app">
          <p>Slack opens with everything filled in. Pick your workspace and confirm.</p>
          <Button
            variant="outline"
            className="min-h-[44px] font-semibold"
            onClick={() => window.open(app.create_url, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Create in Slack
          </Button>
        </Step>
        <Step number={2} title="Brand it">
          <p>
            In the app's <em>Basic Information</em>, set its icon, display name and description.
          </p>
        </Step>
        <Step number={3} title="Allow other workspaces">
          <p>
            Open <em>Manage Distribution</em> and choose <em>Activate Public Distribution</em>. Slack needs this before
            your customers can install it.
          </p>
        </Step>
        <Step number={4} title="Paste its credentials here">
          <p>
            You'll find all four under <em>Basic Information</em>.
          </p>
          <CredentialsForm agentId={genie.id} app={app} />
        </Step>
        <Step number={5} title="Test install">
          <p>Add it to your own workspace. A successful install proves it works, and your app is ready to share.</p>
          <Button
            className={PRIMARY_BUTTON}
            disabled={!app.has_credentials}
            onClick={() => window.open(installLink, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Add to my Slack
          </Button>
        </Step>
      </ol>
      <RemoveApp genie={genie} />
    </div>
  );
}

function CredentialsForm({ agentId, app, onSaved }: { agentId: string; app: OwnSlackApp; onSaved?: () => void }) {
  const { saveCredentials } = useOwnSlackAppActions(agentId);
  const [values, setValues] = useState<OwnSlackAppCredentials>({
    ...EMPTY_CREDENTIALS,
    slackAppId: app.slack_app_id ?? "",
    clientId: app.client_id ?? "",
  });

  const fields: Array<{ key: keyof OwnSlackAppCredentials; label: string; isSecret?: boolean }> = [
    { key: "slackAppId", label: "App ID" },
    { key: "clientId", label: "Client ID" },
    { key: "clientSecret", label: "Client Secret", isSecret: true },
    { key: "signingSecret", label: "Signing Secret", isSecret: true },
  ];

  const handleSave = () => {
    saveCredentials.mutate(values, {
      onSuccess: () => {
        setValues((current) => ({ ...current, clientSecret: "", signingSecret: "" }));
        toast.success("Credentials saved");
        onSaved?.();
      },
      onError: (error: Error) => toast.error(error.message || "Couldn't save the credentials"),
    });
  };

  return (
    <div className="space-y-4">
      {fields.map(({ key, label, isSecret }) => (
        <div key={key}>
          <label htmlFor={`own-slack-${key}`} className="text-sm font-semibold text-gray-700 mb-2 block">
            {label}
          </label>
          <input
            id={`own-slack-${key}`}
            type={isSecret ? "password" : "text"}
            autoComplete="off"
            value={values[key]}
            placeholder={isSecret && app.has_credentials ? "Saved. Paste a new one to replace it" : undefined}
            onChange={(event) => setValues({ ...values, [key]: event.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-genie-primary bg-white text-base sm:text-sm"
          />
        </div>
      ))}
      <Button variant="outline" className="min-h-[44px] font-semibold" onClick={handleSave} disabled={saveCredentials.isPending}>
        {saveCredentials.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        Save credentials
      </Button>
    </div>
  );
}

function ReadyApp({ genie, app }: { genie: Agent; app: OwnSlackApp }) {
  const [isReplacing, setIsReplacing] = useState(false);
  const installLink = installLinkFor(app);

  return (
    <div className="space-y-6">
      <div>
        <p className="font-semibold text-gray-900">Your Slack app is ready</p>
        <p className="text-sm text-gray-600 mt-1">
          Customers who install it see your branding and mention it directly. In a workspace that has it, the shared
          Help Genie app stops answering for {genie.name}, so nobody gets two replies.
        </p>
        <div className="mt-4 flex flex-col sm:flex-row gap-3">
          <Button className={PRIMARY_BUTTON} onClick={() => copyText(installLink, "Install link copied")}>
            <Copy className="w-4 h-4 mr-2" />
            Copy install link
          </Button>
          <Button
            variant="outline"
            className="min-h-[44px] font-semibold px-6"
            onClick={() => window.open(installLink, "_blank", "noopener,noreferrer")}
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            Open in Slack
          </Button>
        </div>
      </div>

      {app.needs_update && <UpdateNotice agentId={genie.id} app={app} />}

      {isReplacing ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Replace credentials</h4>
          <CredentialsForm agentId={genie.id} app={app} onSaved={() => setIsReplacing(false)} />
        </div>
      ) : (
        <button type="button" className="min-h-[44px] text-sm text-genie-primary hover:underline" onClick={() => setIsReplacing(true)}>
          Replace credentials
        </button>
      )}

      <RemoveApp genie={genie} />
    </div>
  );
}

function UpdateNotice({ agentId, app }: { agentId: string; app: OwnSlackApp }) {
  const { markManifestUpdated } = useOwnSlackAppActions(agentId);

  const handleDone = () => {
    markManifestUpdated.mutate(undefined, {
      onSuccess: () => toast.success("Thanks, your Slack app is up to date"),
      onError: () => toast.error("Couldn't save that"),
    });
  };

  return (
    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
      <p className="text-sm font-semibold text-gray-900">Update your Slack app</p>
      <p className="text-sm text-gray-600 mt-1">
        We've added features your app needs new settings for. Copy the new manifest and paste it into your app's{" "}
        <em>App Manifest</em> page in Slack. If Slack asks for new permissions, install your app again.
      </p>
      <div className="mt-3 flex flex-col sm:flex-row gap-3">
        <Button
          variant="outline"
          className="min-h-[44px] font-semibold"
          onClick={() => copyText(JSON.stringify(app.manifest, null, 2), "Manifest copied")}
        >
          <Copy className="w-4 h-4 mr-2" />
          Copy manifest
        </Button>
        <Button variant="outline" className="min-h-[44px] font-semibold" onClick={handleDone} disabled={markManifestUpdated.isPending}>
          I've updated it
        </Button>
      </div>
    </div>
  );
}

function RemoveApp({ genie }: { genie: Agent }) {
  const [isConfirming, setIsConfirming] = useState(false);
  const { remove } = useOwnSlackAppActions(genie.id);

  const handleRemove = () => {
    remove.mutate(undefined, {
      onSuccess: () => toast.success("Your Slack app was removed"),
      onError: () => toast.error("Couldn't remove the app"),
    });
  };

  if (!isConfirming) {
    return (
      <button type="button" className="min-h-[44px] text-sm text-gray-500 hover:underline" onClick={() => setIsConfirming(true)}>
        Remove app
      </button>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-xl border border-gray-200">
      <p className="text-sm text-gray-600">
        {genie.name} stops answering in every workspace that installed this app, and we delete its credentials.
      </p>
      <div className="mt-3 flex gap-2">
        <Button variant="outline" size="sm" className="min-h-[44px]" onClick={() => setIsConfirming(false)}>
          Cancel
        </Button>
        <Button size="sm" className="min-h-[44px] bg-red-600 hover:bg-red-700 text-white" onClick={handleRemove} disabled={remove.isPending}>
          {remove.isPending && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
          Remove app
        </Button>
      </div>
    </div>
  );
}
